/**
 * Formats a loaded quantity value for display by rounding to 2 decimal places.
 * Removes unnecessary trailing zeros and decimal point for whole numbers.
 *
 * @param quantity - The loaded quantity value to format
 * @returns The formatted quantity as a string, or "-" for null/undefined values
 *
 * @example
 * formatLoadedQuantity(123.456) // "123.46"
 * formatLoadedQuantity(100) // "100"
 * formatLoadedQuantity(97.00) // "97"
 * formatLoadedQuantity(97.50) // "97.5"
 * formatLoadedQuantity(null) // "-"
 * formatLoadedQuantity(undefined) // "-"
 */
export function formatLoadedQuantity(
  quantity: number | null | undefined
): string {
  if (quantity === null || quantity === undefined) {
    return "-";
  }

  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(quantity * 100) / 100;
  return rounded.toString();
}

/**
 * Rounds a loaded quantity value to 2 decimal places for display purposes.
 * Unlike formatLoadedQuantity, this returns a number instead of a string.
 *
 * @param quantity - The loaded quantity value to round
 * @returns The rounded quantity as a number, or null/undefined for null/undefined values
 *
 * @example
 * roundLoadedQuantity(123.456) // 123.46
 * roundLoadedQuantity(100) // 100
 * roundLoadedQuantity(null) // null
 * roundLoadedQuantity(undefined) // undefined
 */
export function roundLoadedQuantity(
  quantity: number | null | undefined
): number | null | undefined {
  if (quantity === null || quantity === undefined) {
    return quantity;
  }

  return Math.round(quantity * 100) / 100;
}
